import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout } from '../slices/authSlice';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithLogout = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
    window.location.href = '/login';
  }

  return result;
};

export const planApi = createApi({
  reducerPath: 'planApi',
  baseQuery: baseQueryWithLogout,
  tagTypes: ['Plan'],
  endpoints: (builder) => ({
    // The authenticated merchant's effective plan.
    // Unwraps { success, data: { planKey, feePct, minimums, grandfatheredUntil, status, features } } → data.
    getPlan: builder.query({
      query: () => '/merchant/plan',
      transformResponse: (response) => response?.data || null,
      providesTags: ['Plan'],
    }),

    // Merchant self-service downgrade (Premium → Free), effective at end of billing period.
    downgradePlan: builder.mutation({
      query: () => ({
        url: '/merchant/plan/downgrade',
        method: 'POST',
      }),
      invalidatesTags: ['Plan'],
    }),

    // Admin override: force a merchant onto a plan (Free/Premium/Custom).
    setMerchantPlan: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/admin/merchants/${id}/plan`,
        method: 'POST',
        body,
      }),
    }),

    // Price, carried-forward credit, and the assets Premium may be paid in.
    // The checkout reads chain ids and token contracts from here rather than
    // hard-coding them, so the two cannot drift apart.
    getUpgradeOptions: builder.query({
      query: () => '/merchant/plan/upgrade/options',
      transformResponse: (response) => response?.data || null,
      providesTags: ['Plan'],
    }),

    // Open (or resume) a Premium charge. body: { network: 'ethereum' | 'bsc' }.
    // Resuming matters: a reload must not mint a second charge and strand a
    // part payment on the first.
    upgradeToPremium: builder.mutation({
      query: (body = {}) => ({
        url: '/merchant/plan/upgrade',
        method: 'POST',
        body,
      }),
      transformResponse: (response) => response?.data || null,
    }),

    // Poll an in-flight charge. Carries amountPaidUsd / amountDueUsd so a
    // partially paid charge can show what is left to send.
    getUpgradeStatus: builder.query({
      query: (paymentId) => `/merchant/plan/upgrade/${paymentId}`,
      transformResponse: (response) => response?.data || null,
    }),

    // Hand the backend the hash a connected wallet just signed, so it can read
    // the receipt from the chain instead of waiting on the webhook.
    // Returns { status: 'confirmed' | 'pending', charge? }.
    verifyUpgradeTransaction: builder.mutation({
      query: ({ paymentId, txHash }) => ({
        url: `/merchant/plan/upgrade/${paymentId}/verify`,
        method: 'POST',
        body: { txHash },
      }),
      transformResponse: (response) => response?.data || null,
    }),

    // Pay a charge that the merchant's credit fully covers. Only on an explicit
    // click -- the backend never spends credit by itself.
    useCreditForUpgrade: builder.mutation({
      query: (paymentId) => ({
        url: `/merchant/plan/upgrade/${paymentId}/use-credit`,
        method: 'POST',
      }),
      transformResponse: (response) => response?.data || null,
      invalidatesTags: ['Plan'],
    }),

    // Premium billing / receipt history (newest first).
    getPlanPayments: builder.query({
      query: () => '/merchant/plan/payments',
      transformResponse: (response) => response?.data || [],
      providesTags: ['Plan'],
    }),
  }),
});

export const {
  useGetPlanQuery,
  useDowngradePlanMutation,
  useSetMerchantPlanMutation,
  useGetUpgradeOptionsQuery,
  useUpgradeToPremiumMutation,
  useGetUpgradeStatusQuery,
  useVerifyUpgradeTransactionMutation,
  useUseCreditForUpgradeMutation,
  useGetPlanPaymentsQuery,
} = planApi;
